/**
 * Formatting + calendar helpers shared by the engine and UI.
 * Day index 0 == January 1, 2024 (game epoch).
 */

const EPOCH = Date.UTC(2024, 0, 1);

export function dayToDate(day: number): Date {
  return new Date(EPOCH + day * 86_400_000);
}

export function dateToDay(date: Date): number {
  return Math.floor((date.getTime() - EPOCH) / 86_400_000);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** e.g. "Aug 22, 2026" */
export function formatGameDate(day: number): string {
  const d = dayToDate(day);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

/** e.g. "Q3 2026" */
export function quarterLabel(day: number): { year: number; quarter: number; label: string } {
  const d = dayToDate(day);
  const year = d.getUTCFullYear();
  const quarter = Math.floor(d.getUTCMonth() / 3) + 1;
  return { year, quarter, label: `Q${quarter} ${year}` };
}

/** Compact money: $1.9k, $84.2k, $1.24M, -$12.5k */
export function money(n: number, opts: { compact?: boolean; sign?: boolean } = {}): string {
  const neg = n < 0;
  const abs = Math.abs(n);
  let out: string;
  if (opts.compact !== false) {
    if (abs >= 1_000_000_000) out = `$${(abs / 1_000_000_000).toFixed(2)}B`;
    else if (abs >= 1_000_000) out = `$${(abs / 1_000_000).toFixed(2)}M`;
    else if (abs >= 10_000) out = `$${(abs / 1_000).toFixed(1)}k`;
    else out = `$${abs.toFixed(abs >= 100 ? 0 : 2)}`;
  } else {
    out = `$${Math.round(abs).toLocaleString('en-US')}`;
  }
  if (opts.sign && !neg && n > 0) out = `+${out}`;
  return neg ? `-${out}` : out;
}

export function pct(n: number, digits = 1): string {
  return `${(n * 100).toFixed(digits)}%`;
}

export function num(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

export function clamp(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}
